import axios from "axios";
import React, { useState, useEffect } from "react";
import { BASE_URL } from "../../../env";
import Sidebar from "../../../components/layout/Sidebar";
import { Form, Row, Col } from "react-bootstrap";
import {
  calculateEmiAmount,
  getEffectiveEmi,
  generateLoanSummaryForMember,
  getReportWindow,
  isLoanCovered,
  calculateInsuranceAmount,
  formatAmount,
  formatISODateToLocalDMY,
} from "../../../utils/utils";
import CustomPagination from "../../../components/ui/CustomPagination";
import DataTable from "../../../components/ui/DataTable";

import { useAdminAuth } from "../../auth/AdminAuthProvider";

import DashboardHeader from "../../../components/layout/DashboardHeader";
import { MdExpandMore } from "react-icons/md";
import StandardizedDateInput from "../../../components/ui/StandardizedDateInput";

const MasterDataReport = () => {
  const { adminUser } = useAdminAuth();
  const [reportData, setReportData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  const [openSidebarToggle, setOpenSidebarToggle] = useState(window.innerWidth > 1000);
  const [currentPage, setCurrentPage] = useState(1);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [fromDate, setFromDate] = useState("2024-07-01");
  const [toDate, setToDate] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const itemsPerPage = 20;

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const paginationData = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  const OpenSidebar = () => {
    setOpenSidebarToggle(!openSidebarToggle);
  };

  useEffect(() => {
    const getReportData = async (id) => {
      try {
        const response = await axios.get(
          `${BASE_URL}/api/getMasterReportData`,
          { params: { id } }
        );
        // console.log(
        //   "MasterData response.data: " + JSON.stringify(response.data)
        // );
        if (response.data && Array.isArray(response.data)) {
          setReportData(response.data);
        } else if (response.data && Array.isArray(response.data.data)) {
          setReportData(response.data.data);
        } else {
          setReportData([]);
        }
      } catch (error) {
        console.log("Error fetching Data:", error);
        setReportData([]);
      }
    };

    if (adminUser?.user?.id || adminUser?.id) {
      getReportData(adminUser?.user?.id || adminUser?.id);
    }
  }, [adminUser]);

  // Automatically filter data when fromDate, toDate, or searchTerm changes
  useEffect(() => {
    const safeReportData = Array.isArray(reportData) ? reportData : [];
    // Process all data first and calculate defaults
    const processedData = safeReportData.map((item) => {
      const loanSummary = generateLoanSummaryForMember(
        item,
        "2000-01-01",
        toDate || new Date().toISOString().split("T")[0]
      );

      return {
        ...item,
        ...loanSummary,
      };
    });

    let filtered = processedData;

    if (fromDate && toDate) {
      // Shared whole-day window, so Master and Outstanding cannot drift apart.
      const { start, end } = getReportWindow(fromDate, toDate);

      filtered = filtered.filter((item) => {
        const updatedAt = new Date(item.branchManagerStatusUpdatedAt);
        return updatedAt >= start && updatedAt <= end;
      });
    }

    if (searchTerm && searchTerm.trim() !== "") {
      const term = searchTerm.trim().toLowerCase();
      filtered = filtered.filter((item) => {
        const memberName = String(item.memberName || "").toLowerCase();
        const customerId = String(item.customerId || "").toLowerCase();
        const loanId = String(item.loanId || "").toLowerCase();
        const phoneNumber = String(item.phoneNumber || "").toLowerCase();
        const aadharNo = String(item.aadharNo || "").toLowerCase();
        const applicationId = String(item.ApplicationId || "").toLowerCase();

        return (
          memberName.includes(term) ||
          customerId.includes(term) ||
          loanId.includes(term) ||
          phoneNumber.includes(term) ||
          aadharNo.includes(term) ||
          applicationId.includes(term)
        );
      });
    }

    setFilteredData(filtered);
    setCurrentPage(1);
  }, [fromDate, toDate, reportData, searchTerm]);

  // Add these new columns to your existing columns array
  const columns = [
    { label: "S No", render: (data, index) => index + 1 },
    { label: "Region", render: (data) => data.regionName },
    { label: "Division", render: (data) => data.divisionName },
    { label: "Branch Name", render: (data) => data.branchName },
    { label: "Branch Code", render: (data) => data.branchCode },
    { label: "Customer Name", render: (data) => data.memberName },
    { label: "Customer ID", render: (data) => data.customerId },
    {
      label: "Address",
      render: (data) =>
        `${data.permanentDoorNo || ""},${data.permanentStreetNo || ""},${data.permanentPanchayat || ""},${data.permanentTaluk || ""},${data.permanentDistrict || ""}-${data.permanentPincode || ""}`,
    },
    { label: "Contact No", render: (data) => data.phoneNumber },
    { label: "Loan Type", render: (data) => data.loanType },
    { label: "Loan Status", render: (data) => data.loanStatus },

    {
      label: "Sub Category",
      render: (data) => data.businessDetails?.natureOfBusiness || "N/A",
    },
    { label: "Loan Cycle", render: (data) => data.loanCycle },
    { label: "Funder Name", render: (data) => data.agencyName },
    { label: "Loan Account No", render: (data) => data.loanId },
    {
      label: "Rate of Interest",
      render: (data) => data.proposedLoanDetails?.rateOfInterest || 0,
    },
    {
      label: "Disbursement Date",
      render: (data) =>
        data.branchManagerStatus === "disbursed" &&
          data.branchManagerStatusUpdatedAt
          ? new Date(data.branchManagerStatusUpdatedAt).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          })
          : "",
    },
    {
      label: "Maturity Date",
      render: (data) => {
        return data.lastEmiDate ? formatISODateToLocalDMY(data.lastEmiDate) : "";
      },
    },
    {
      label: "Disbursed Amount",
      render: (data) =>
        data.branchManagerStatus === "disbursed"
          ? formatAmount(data.sanctionedLoanAmountBySanctionCommittee || 0)
          : 0,
    },
    {
      label: "Tenure In Months",
      render: (data) => data.proposedLoanDetails?.tenureInMonths || 0,
    },
    {
      label: "EMI months Paid",
      render: (data) => data.emiMonthsPaid || 0,
    },
    { label: "Employee Name", render: (data) => data.employeeName },
    { label: "Employee Code", render: (data) => data.employeeId },

    {
      label: "Outstanding Principal",
      render: (data) => formatAmount(data.totalOutstandingPrincipal || 0),
    },
    {
      label: "Outstanding Interest",
      render: (data) => formatAmount(data.totalOutstandingInterest || 0),
    },
    {
      label: "Principal Paid",
      render: (data) => formatAmount(data.principalPaid || 0),
    },
    {
      label: "Interest Paid",
      render: (data) => formatAmount(data.interestPaid || 0),
    },
    {
      label: "Overdue Principal",
      render: (data) => formatAmount(data.overduePrincipal || 0),
    },
    {
      label: "Overdue Interest",
      render: (data) => formatAmount(data.overdueInterest || 0),
    },
    {
      label: "Total PAR",
      render: (data) => formatAmount(data.totalPAR || 0),
    },
    {
      label: "DPD",
      render: (data) => data.dpd || 0,
    },
    {
      label: "Bucket",
      render: (data) => data.bucket || "-",
    },
    {
      label: "Last Paid Date",
      render: (data) => data.lastPaidDate || "-",
    },
    {
      label: "First Overdue Date",
      render: (data) => data.firstOverdueDate || "-",
    },
    {
      label: "Is Demised(Insurance)",
      render: (data) => "No",
    },
    {
      label: "EMI Start Date",
      render: (data) => {
        return data.firstEmiDate ? formatISODateToLocalDMY(data.firstEmiDate) : "";
      },
    },
    { label: "Prospect ID", render: (data) => data.ApplicationId },
    {
      label: "Processing Fee",
      render: (data) => formatAmount(data.processingCharge || 0),
    },
    { label: "GST", render: (data) => formatAmount(data.gst || 0) },
    {
      label: "Insurance",
      render: (data) =>
        formatAmount(
          isLoanCovered(data)
            ? calculateInsuranceAmount(
              data.sanctionedLoanAmountBySanctionCommittee,
              data.proposedLoanDetails?.tenureInMonths || 0
            )
            : 0
        ),
    },
    {
      // Money written off when a foreclosure was settled below the payable
      // amount. Blank for loans that were never foreclosed.
      label: "Waiver Amount",
      render: (data) =>
        data?.fk_member_details_hasOne_member_foreclosure_approval_memberId
          ?.waiverAmount ?? "",
    },
    {
      label: "Waiver Reason",
      render: (data) =>
        data?.fk_member_details_hasOne_member_foreclosure_approval_memberId
          ?.waiverReason || "",
    },
    {
      label: "Security Deposit",
      render: (data) => formatAmount(data.securityDeposit || 0),
    },
    {
      label: "EMI Amount",
      render: (data) => {
        const emiAmount = getEffectiveEmi(data);
        return formatAmount(emiAmount || 0);
      },
    },
    {
      label: "Loan Purpose",
      render: (data) => data.businessDetails?.businessType || "N/A",
    },
    {
      label: "Aadhar Number",
      render: (data) => data.aadharNo,
    },
    {
      label: "Pan Number",
      render: (data) => (data.panNo ? data.panNo : ""),
    },
    {
      label: "Voter Id",
      render: (data) =>
        data.anotherIdentity === "Voter Id" ? data.anotherIdentityId : "",
    },
    {
      label: "Bank Account Number",
      render: (data) => data.bankDetails?.accountNumber || "N/A",
    },
    {
      label: "IFSC Code",
      render: (data) => data.bankDetails?.ifscCode || "N/A",
    },
    {
      label: "Bank Name",
      render: (data) => data.bankDetails?.bankName || "N/A",
    },
    {
      label: "Bank Branch",
      render: (data) => data.bankDetails?.bankBranch || "N/A",
    },
  ];

  return (
    <section className="master-layout">
      <div className="master-sidebar-wrapper">
        <Sidebar
          openSidebarToggle={openSidebarToggle}
          OpenSidebar={OpenSidebar}
        />
      </div>

      <div className="master-content-wrapper">
        <div className="master-header-wrapper">
          <DashboardHeader OpenSidebar={OpenSidebar} />
        </div>

        <div className="master-card-container">
          <div className="master-search-section">
            <h1 className="TableHeaderText w-100 text-start">Master Data Report</h1>

            <button
              className={`filter-toggle-btn ${showMobileFilters ? "active" : ""}`}
              onClick={() => setShowMobileFilters(!showMobileFilters)}
            >
              <span style={{ fontWeight: 600, fontSize: "14px" }}>
                {showMobileFilters ? "Hide Filters" : "Show Filters"}
              </span>
              <MdExpandMore size={22} />
            </button>

            <div className={`filter-container ${!showMobileFilters ? "mobile-hidden" : "mobile-visible"}`}>
              <Row>
                <Col lg={4} md={6} xs={12} className="mb-1">
                  <Form.Group>
                    <Form.Label>Search Customer</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Search Name, Customer ID, Loan Acc No, Phone..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col lg={3} md={6} xs={12}>
                  <Form.Group>
                    <Form.Label>From Date</Form.Label>
                    <StandardizedDateInput
                      value={fromDate}
                      onChange={(event) => {
                        const val = event.target.value;
                        setFromDate(val);
                        if (val && toDate) {
                          setTimeout(() => setShowMobileFilters(false), 300);
                        }
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col lg={3} md={6} xs={12}>
                  <Form.Group>
                    <Form.Label>To Date</Form.Label>
                    <StandardizedDateInput
                      value={toDate}
                      onChange={(event) => {
                        const val = event.target.value;
                        setToDate(val);
                        if (fromDate && val) {
                          setTimeout(() => setShowMobileFilters(false), 300);
                        }
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>
          </div>

          <div className="master-list-section">
            <div className="master-table-wrapper" style={{ padding: "0 15px" }}>
              <DataTable
                columns={columns}
                data={paginationData}
                reportName="MasterDataReport"
                excelData={filteredData}
              />
            </div>

            <div className="master-pagination-wrapper">
              <CustomPagination
                totalPages={totalPages}
                currentPage={currentPage}
                paginate={paginate}
                totalItems={filteredData.length}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MasterDataReport;
